import { Flex } from 'components/Flex';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { formatCurrencyCAD } from 'format/formatCurrencyCAD';
import { FC, Fragment, useMemo } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { formatInterval, calculateShiftDurationInMinutes, formatTimeFromISO } from 'utils/time';
import {
  AbsentShiftDisableMessage,
  LabelText,
  ValueText,
  TimeSheetsStartEndTimeContainer,
} from '../DetailView.styled';
import { formatUnpaidBreakTime } from './TimesheetsTab.utils';
import { useTimeSheetsTabDataContext } from './TimesheetsTabData.context';
import { useTimesheetsTabFormContext } from './TimesheetsTabForm.context';
import { BKJComboBox } from '@bookjane2/bookjane-design-library';
import { IBKJComboBoxOption } from '@bookjane2/bookjane-design-library/lib/components/BKJComboBox/BKJComboBox.types';
import { FormatCurrency } from 'components/FormatCurrency';
import { OrderStatusEnum } from 'types/graphql';
import { useSelector } from 'react-redux';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';
import { ShiftTimeSelectInput } from 'components/ShiftTimeSelectInput';

const marginLeft = 16;

const breakTimeDropdownHrs: IBKJComboBoxOption[] = Array.from({ length: 5 }, (_, i) => {
  const hr = i.toString().padStart(2, '0');
  return {
    label: `${i} hr${i === 1 ? '' : 's'}`,
    value: hr,
    key: hr,
    id: hr,
  };
});

const breakTimeDropdownMins: IBKJComboBoxOption[] = Array.from({ length: 60 }, (_, i) => {
  const min = i.toString().padStart(2, '0');
  return {
    label: `${min} min${i === 1 ? '' : 's'}`,
    value: min,
    key: min,
    id: min,
  };
});

export const TimesheetsTabPendingView: FC = () => {
  const { t } = useTranslation();
  const { onChange, values } = useTimesheetsTabFormContext();
  const { data } = useTimeSheetsTabDataContext();
  const { data: shiftModalData } = useShiftModalDataContext();
  const order = shiftModalData!.order;
  const is24hTimeFormat = useSelector(isUserTimeFormat24hSelector);

  const totalPaidDurationInMinutes = useMemo(
    () =>
      calculateShiftDurationInMinutes({
        startDate: order.startDate,
        startAt: values.startTime,
        endAt: values.endTime,
        unpaidBreakTime: [`${values.unpaidBreakHrs[0]}:${values.unpaidBreakMins[0]}`],
        isPaidDuration: true,
      }),
    [order, values.endTime, values.startTime, values.unpaidBreakHrs, values.unpaidBreakMins],
  );

  const totalDurationInMinutes = useMemo(
    () =>
      calculateShiftDurationInMinutes({
        startDate: order.startDate,
        startAt: values.startTime,
        endAt: values.endTime,
        isPaidDuration: false,
      }),
    [order, values.endTime, values.startTime],
  );
  const rawAmount =
    (totalPaidDurationInMinutes / 60) *
    (data?.fetchTimeSheet?.timeSheet?.acceptedHourlyRate?.rawCurrency || 0);

  const isAbsentShift = order.status.value === OrderStatusEnum.absent;

  return (
    <Flex flexDirection="column" childMarginBottom="32px">
      {isAbsentShift && (
        <Fragment>
          <AbsentShiftDisableMessage>
            {t('timesheets:ABSENT_SHIFT_DISABLED_MESSAGE_PART1')}
            <br />
            <b>{t('timesheets:ABSENT_SHIFT_DISABLED_MESSAGE_ABSENT')}</b>,&nbsp;
            {t('timesheets:ABSENT_SHIFT_DISABLED_MESSAGE_PART2')}&nbsp;
            <b>0</b>.
          </AbsentShiftDisableMessage>
        </Fragment>
      )}
      <TimeSheetsStartEndTimeContainer childMarginRight="5px">
        <Flex flexDirection="column" width="250px">
          {data.fetchTimeSheet.timeSheet?.status.value === 'pending' ? (
            <ShiftTimeSelectInput
              label={t('shift_modal:START_TIME')}
              placeholder={t('shift_modal:START_TIME')}
              name="startTime"
              value={values.startTime}
              onChange={onChange}
              disabled={isAbsentShift}
            />
          ) : (
            <ValueText marginLeft={marginLeft}>
              {formatTimeFromISO(data.fetchTimeSheet.startTime, is24hTimeFormat)}
            </ValueText>
          )}
        </Flex>
        <Flex flexDirection="column" width="245px">
          {data.fetchTimeSheet.timeSheet?.status.value === 'pending' ? (
            <ShiftTimeSelectInput
              label={t('shift_modal:END_TIME')}
              placeholder={t('shift_modal:END_TIME')}
              name="endTime"
              value={values.endTime}
              onChange={onChange}
              disabled={isAbsentShift}
            />
          ) : (
            <ValueText marginLeft={marginLeft}>
              {formatTimeFromISO(data.fetchTimeSheet.endTime, is24hTimeFormat)}
            </ValueText>
          )}
        </Flex>
      </TimeSheetsStartEndTimeContainer>
      <Flex flexDirection="column" width="250px">
        {data.fetchTimeSheet.timeSheet?.status.value === 'pending' ? (
          <Fragment>
            <Flex padding="0 0 10px 0">{t('shift_modal:UNPAID_BREAK')}</Flex>
            <Flex flexDirection="row" gap="5px">
              <Flex width="210px">
                <BKJComboBox
                  placeholder={t('shift_modal:HR_PLACEHOLDER')}
                  name="unpaidBreakHrs"
                  value={values.unpaidBreakHrs}
                  onChange={onChange}
                  options={breakTimeDropdownHrs}
                  type="Single"
                  action="OpenToggle"
                  disabled={isAbsentShift}
                  filter="TimeStartsWith"
                />
              </Flex>
              {!isAbsentShift && (
                <BKJComboBox
                  placeholder={t('shift_modal:MIN_PLACEHOLDER')}
                  name="unpaidBreakMins"
                  value={values.unpaidBreakMins}
                  onChange={onChange}
                  options={breakTimeDropdownMins}
                  type="Single"
                  action="OpenToggle"
                  filter="TimeStartsWith"
                  $dropdownHeight="240px"
                />
              )}
            </Flex>
          </Fragment>
        ) : (
          <Fragment>
            <LabelText>{t('shift_modal:UNPAID_BREAK')}</LabelText>
            <ValueText marginLeft={marginLeft}>
              {formatUnpaidBreakTime(data?.fetchTimeSheet?.timeSheet?.unpaidBreakTime as string)}
            </ValueText>
          </Fragment>
        )}
      </Flex>
      <Flex>
        <Flex flexDirection="column" width="240px">
          <LabelText>{t('shift_modal:TOTAL_DURATION')}</LabelText>
          <ValueText marginLeft={marginLeft}>
            {isAbsentShift ? '0' : formatInterval(totalDurationInMinutes)}
          </ValueText>
        </Flex>
        <Flex flexDirection="column" width="240px">
          <LabelText>{t('shift_modal:TOTAL_PAID_DURATION')}</LabelText>
          <ValueText marginLeft={marginLeft}>
            {data.fetchTimeSheet.timeSheet?.approvedTotalTime?.time}
          </ValueText>
        </Flex>
      </Flex>
      <Flex>
        <Flex flexDirection="column" width="240px">
          <LabelText>{t('shift_modal:HOURLY_PAY_RATE')}</LabelText>
          <ValueText marginLeft={marginLeft}>
            {formatCurrencyCAD(
              data?.fetchTimeSheet?.timeSheet?.acceptedHourlyRate?.rawCurrency || 0,
            )}
          </ValueText>
        </Flex>
        <Flex flexDirection="column" width="240px">
          <LabelText>{t('shift_modal:TOTAL_PAY')}</LabelText>
          <ValueText marginLeft={marginLeft}>
            <FormatCurrency amount={`${isAbsentShift ? 0 : rawAmount}`} />
          </ValueText>
        </Flex>
      </Flex>
    </Flex>
  );
};
